export type LsrcCode = "ACQ" | "MAIL" | "SIS" | "UTM" | "ORG" | "UTM";

export type LsrcPriority = {
  [K in LsrcCode]: number;
};

export type LsrcLists = {
  [K in LsrcCode]: string[];
};

export const Resources = {
  appName: "Afforda",
  riskThreshold: 0.65,

  lsrc_priority: {
    ACQ: 1, // Leads acquired from ads and LS/Drafty rejects
    MAIL: 2, // Leads acquired from mailer campaigns
    SIS: 3, // Leads acquried from sister brand pages
    UTM: 4, // Leads acquired from any other utm sources
    ORG: 5, // Leads acquired organically
    LSD: 6, // Direct sources
  } as const,

  ls_inclusion: {
    ACQ: ["gads", "ls_den", "df_den"],
    MAIL: ["email"],
    SIS: [
      "ls_benefitglance",
      "drafty_benefitglance",
      "ls_resources",
      "drafty_resources",
      "ls_chatbot",
      "drafty_chatbot",
      "drafty_loan_chatbot",
    ],
    UTM: ["utm"],
    ORG: ["organic"],
    LSD: ["direct"],
  } as const,

  ls_exclusion: {
    ACQ: [],
    MAIL: [],
    SIS: [],
    UTM: [],
    ORG: [],
    LSD: [],
  } as const,

  useOfFunds: {
    Gambling: "other",
    "Moving costs": "moving_costs",
    Repairs: "emergency_cash",
    "One-off purchase": "one_off_purchase",
    "Irregular bill payments": "irregular_bills",
    "Unexpected expenses": "unexpected_expenses",
    "Pay off another loan": "debt_consolidation",
    "Travel expenses": "travel_expense",
  } as const,

  titleItems: {
    Mr: "mr",
    Miss: "miss",
    Mrs: "mrs",
    Ms: "ms",
  } as const,

  howLongOptions: {
    "1-3 months": "3",
    "4-6 months": "6",
    "7-12 months": "12",
    "1-2 years": "24",
    "2-4 years": "48",
    "Over 4 years": "60",
  } as const,

  housingTypeOptions: {
    "A homeowner": "owner",
    "Renting privately": "tenant",
    "Renting from the council": "council_tenant",
    "Renting from a housing association": "housing_association",
    "Living with relatives": "living_with_parents",
    "Living rent-free": "rent_free",
    "Staying at your employer": "staying_at_employer",
    "Supported or exempt": "supported_or_exempt",
    "Boarder or lodger": "boarder_or_lodger",
    Homeless: "homeless",
    "Shared ownership": "shared_ownership",
  } as const,

  maritalStatusOptions: {
    Single: "single",
    Married: "married",
    "Living together": "living_with_partner",
    Divorced: "divorced",
    Separated: "separated",
    Widowed: "widowed",
    Other: "other",
  } as const,

  numberOfDependantsOptions: {
    "0": "0",
    "1": "1",
    "2": "2",
    "3": "3",
    "4": "4",
    "5": "5",
    "6": "6",
    "7": "7",
    "8": "8",
    "9": "9",
  } as const,

  employmentTypeOptions: {
    "Employed full-time": "full_time",
    "Employed part-time": "part_time",
    Temping: "temporary_employment",
    "Self-employed": "self_employed",
    "Receiving benefits": "disability_benefit",
    Retired: "retired",
    Unemployed: "unemployed",
  } as const,

  employmentLengthOptions: {
    "1-3 months": "3",
    "4-6 months": "6",
    "7-12 months": "12",
    "1-2 years": "24",
    "2-4 years": "48",
    "Over 4 years": "60",
  } as const,

  payFrequencyOptions: {
    "Last working day of the month": "last_working_day_of_month",
    "Last monday of the month": "last_monday_of_month",
    "Last tuesday of the month": "last_tuesday_of_month",
    "Last wednesday of the month": "last_wednesday_of_month",
    "Last thursday of the month": "last_thursday_of_month",
    "Last friday of the month": "last_friday_of_month",
    "Specific day of the month": "specific_day_of_month",
    "Four weekly": "four_weekly",
    Weekly: "weekly",
    Fortnightly: "fortnightly",
    "Semi monthly": "semi_monthly",
  } as const,

  numberOfChildrenOptions: {
    "0": "0",
    "1": "1",
    "2": "2",
    "3": "3",
    "4": "4",
    "5": "5",
    "6": "6",
    "7": "7",
    "8": "8",
    "9": "9",
    "10": "10",
  } as const,

  immediateConfig: {
    title: "Here are some available options for you",
    subtitle: "Based on what you’ve told us",
    userNeeds: [
      "You need to make a one-off purchase",
      "You need to take care of this soon",
    ],
    helpText: "Here’s how we think Afforda can best help",
    improvementTitle: "Improve your finances",
    improvementText:
      "We’d like to help you get into a better position, for the next time something unexpected happens.",
  },

  sectionConfigurations: {
    retailDiscounts: {
      sectionHeader: "Exclusive discounts",
      sectionText: "Discounts available to help with your purchases.",
      ctaText: "Reveal your discounts",
    },
    loan: {
      sectionHeader: "Explore credit options",
      sectionText: "If you need credit, Afforda could help you find a lender.",
      ctaText: "Check if credit options are available",
      subText: "Representative 1333%APR",
    },
    grants: {
      sectionHeader: "Check your eligibility for Government and local grants",
      sectionText:
        "It’s possible that you could be eligible for Government or local grants.",
      ctaText: "Check for available grants",
    },
    savingsAccount: {
      sectionHeader: "Setup savings account",
      sectionText:
        "Preparing for unexpected expenses is key. Click below for more information on savings accounts, and how to start saving.",
      ctaText: "Open your savings account today",
    },
    checkBenefits: {
      sectionHeader: "Check benefits eligibility",
      sectionText:
        "Over £17 billion of Government benefits are currently unclaimed in the UK. It’s possible you could be eligible for some.",
      ctaText: "Check Government benefits",
    },
    saveOnShopping: {
      sectionHeader: "Try Emma App",
      sectionText:
        "Try the Emma app to help track spending and budget for the future",
      ctaText: "Learn more about Emma",
    },
    loqbox: {
      sectionHeader: "Join Loqbox",
      sectionText:
        "Build credit, the easy way. Loqbox helps you improve your score, with no hard credit check.",
      ctaText: "Get 7 days free",
    },
  } as const,

  signpostingData: {
    gambling: {
      title: "GAMBLING SUPPORT",
      messages: [
        "If gambling is affecting you or someone close, you’re not alone. Talking to someone can help you see things more clearly and feel more in control. Visit BeGambleAware for free, confidential support.",
      ],
      helpLink: "https://www.begambleaware.org/",
      organizationName: "BeGambleAware",
      phoneNumber: "0808 8020 133",
    },
    over_indebtedness: {
      title: "FINANCIAL SUPPORT",
      messages: [
        "If you’re finding it hard to manage money or debt, MoneyHelper offers free, practical guidance. Their tools and calculators can help you take the next step toward control and confidence.",
      ],
      helpLink:
        "https://www.moneyhelper.org.uk/en/money-troubles/dealing-with-debt/use-our-debt-advice-locator",
      organizationName: "MoneyHelper",
      ctaText: "Get Free Debt Advice",
      phoneNumber: "0800 138 1111",
    },
    insolvency: {
      title: "INSOLVENCY SUPPORT",
      messages: [
        "If you’re facing insolvency or exploring options like an IVA, trust deed, bankruptcy, or debt relief order, clear guidance is available. Visit the Insolvency Service for free information to help you understand your choices and make informed, confident decisions.",
      ],
      helpLink:
        "https://www.gov.uk/government/organisations/insolvency-service",
      organizationName: "Insolvency Service",
      phoneNumber: "0300 678 0015",
    },
    mental_health_sad: {
      title: "WELL-BEING SUPPORT",
      messages: [
        "Your mind matters. If things feel heavy right now, reaching out can help you feel lighter and more in control. Visit Mind for free, confidential mental health support and practical guidance.",
      ],
      helpLink: "https://www.mind.org.uk/",
      organizationName: "Mind",
      phoneNumber: "0300 123 3393",
    },
    mental_health_risk: {
      title: "IMMEDIATE SUPPORT",
      messages: [
        "If you’re in crisis or having thoughts of self-harm, your safety comes first. You’re not alone. Visit Samaritans for free, 24/7 confidential support from someone who’ll listen and help you stay safe.",
      ],
      helpLink: "https://www.samaritans.org/",
      organizationName: "Samaritans",
      phoneNumber: "116 123",
    },
    addiction: {
      title: "RECOVERY SUPPORT",
      messages: [
        "If you’re concerned about addiction or substance use, you’re not alone. Visit Frank for free, confidential advice and guidance on safe, effective treatment options.",
      ],
      helpLink: "https://www.talktofrank.com/",
      organizationName: "FRANK",
      phoneNumber: "0300 123 6600",
    },
  } as const,

  allCategories: [
    { id: 1, name: "Groceries", slug: "groceries" },
    { id: 2, name: "Food and Drink", slug: "food-and-drink" },
    { id: 3, name: "Homeware", slug: "home-and-furniture" },
    { id: 4, name: "Electronics", slug: "electronics" },
    { id: 5, name: "Retail and Stores", slug: "retail" },
    { id: 6, name: "Fashion", slug: "fashion" },
    { id: 7, name: "Travel", slug: "travel" },
    { id: 8, name: "Health & Fitness", slug: "health-and-fitness" },
    { id: 9, name: "Beauty & Personal Care", slug: "beauty" },
    { id: 10, name: "Gifts", slug: "gifts" },
    { id: 11, name: "Automotive", slug: "automotive" },
    { id: 12, name: "Entertainment", slug: "entertainment" },
  ] as const,

  govtGrantsUrl:
    "https://www.find-government-grants.service.gov.uk?utm_source=afforda",
} as const;

// Export types for form values
export type UseOfFundsValue =
  (typeof Resources.useOfFunds)[keyof typeof Resources.useOfFunds];
export type TitleValue =
  (typeof Resources.titleItems)[keyof typeof Resources.titleItems];
export type HowLongValue =
  (typeof Resources.howLongOptions)[keyof typeof Resources.howLongOptions];
export type HousingTypeValue =
  (typeof Resources.housingTypeOptions)[keyof typeof Resources.housingTypeOptions];
export type MaritalStatusValue =
  (typeof Resources.maritalStatusOptions)[keyof typeof Resources.maritalStatusOptions];
export type EmploymentTypeValue =
  (typeof Resources.employmentTypeOptions)[keyof typeof Resources.employmentTypeOptions];
export type EmploymentLengthValue =
  (typeof Resources.employmentLengthOptions)[keyof typeof Resources.employmentLengthOptions];
export type PayFrequencyValue =
  (typeof Resources.payFrequencyOptions)[keyof typeof Resources.payFrequencyOptions];
